import React from "react";
import { Box, Chip, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { PROGRAM_COLORS } from "../programLensConstants";
import ConditionCareNotes from "./ConditionCareNotes";

const C = PROGRAM_COLORS.CCM;

function CcmSummaryCard({
  patientSummary,
  apiBase,
  patientId,
  onShowSnackbar,
  onAddGoal,
  onEditGoal,
  onAddBarrier,
  onEditBarrier,
  onAddCondition,
}) {
  const goals = patientSummary?.goals || [];
  const barriers = patientSummary?.barriers || [];
  const medications = patientSummary?.medications || [];
  const carePlans = patientSummary?.care_plans || [];
  const encounters = patientSummary?.encounters || [];

  const statusColor = (status) => {
    if (status === "achieved" || status === "resolved" || status === "active") return "#4ade80";
    if (status === "in_progress") return "#fbbf24";
    return "#888";
  };

  return (
    <Paper
      sx={{
        border: `2px solid ${C.main}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: `${C.main}06`,
      }}
    >
      <Box
        sx={{
          bgcolor: `${C.main}18`,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${C.main}25`,
        }}
      >
        <FavoriteIcon sx={{ fontSize: 18, color: C.main }} />
        <Typography sx={{ color: C.main, fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5 }}>
          CCM SUMMARY
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", ml: "auto" }}>
          Chronic Care Management
        </Typography>
      </Box>

      <Box sx={{ p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {/* Conditions with care notes */}
        <ConditionCareNotes
          problems={patientSummary?.problems}
          patientId={patientId}
          apiBase={apiBase}
          accentColor={C}
          onShowSnackbar={onShowSnackbar}
          onAddCondition={onAddCondition}
        />

        {/* Goals */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              GOALS
            </Typography>
            {onAddGoal && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="Add Goal"
                size="small"
                onClick={onAddGoal}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {goals.length === 0 && (
            <Typography variant="caption" color="text.secondary">No goals</Typography>
          )}
          {goals.map((g) => (
            <Box
              key={g.id}
              sx={{ px: 1, py: 0.5, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8rem" }}>{g.description || g.goal_description}</Typography>
              <Chip label={g.status} size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${statusColor(g.status)}20`, color: statusColor(g.status) }} />
              {onEditGoal && (
                <IconButton size="small" aria-label="Edit goal" onClick={() => onEditGoal(g)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        {/* Barriers */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              BARRIERS
            </Typography>
            {onAddBarrier && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="Add Barrier"
                size="small"
                onClick={onAddBarrier}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {barriers.length === 0 && (
            <Typography variant="caption" color="text.secondary">No barriers</Typography>
          )}
          {barriers.map((b) => (
            <Box
              key={b.id}
              sx={{ px: 1, py: 0.5, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8rem" }}>
                {b.barrier_type || b.type} — {b.description}
              </Typography>
              <Chip label={b.status} size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${statusColor(b.status)}20`, color: statusColor(b.status) }} />
              {onEditBarrier && (
                <IconButton size="small" aria-label="Edit barrier" onClick={() => onEditBarrier(b)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        {/* Medications + Care Plan */}
        <Box>
          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.75 }}>
            KEY MEDICATIONS
          </Typography>
          {medications.slice(0, 5).map((m, i) => (
            <Typography key={i} sx={{ fontSize: "0.8rem", color: "text.primary" }}>
              {m.drug || m.name}{" "}
              <Box component="span" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                {m.dose} {m.frequency}
              </Box>
            </Typography>
          ))}

          <Divider sx={{ my: 1 }} />

          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.5 }}>
            CARE PLAN
          </Typography>
          {carePlans.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No active care plan</Typography>
          ) : (
            carePlans.slice(0, 2).map((cp) => (
              <Box key={cp.id}>
                <Typography sx={{ fontSize: "0.8rem" }}>
                  {cp.title}{" "}
                  <Chip label={cp.status} size="small" sx={{ height: 16, fontSize: "0.55rem", bgcolor: `${C.main}15`, color: C.main, ml: 0.5 }} />
                </Typography>
                {encounters[0] && (
                  <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                    Last encounter: {new Date(encounters[0].encounter_date || encounters[0].created_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default CcmSummaryCard;
